<script setup lang="ts">
import type { Property } from '@/core/entities/property';
import type { BreadcrumbItem } from '~/components/ui/breadcrumb';
import type {
	NearRegion,
	Region,
	RegionContent
} from '~~/core/entities/Region';

import { getCacheAsyncDataConfig } from '@/utils';
import { useEvents } from '~/composables/events/useEvents';
import { MixpanelProps } from '~/core/service/mixpanel';
import { nuxtPropertiesRepository } from '~~/adapters/repositories/nuxt/nuxtPropertiesRepository';
import { REGION_SERVICE } from '~~/utils/symbols';

const regionService = inject(REGION_SERVICE)!;

const route = useRoute();

definePageMeta({
	key: route => route.path,
	layout: 'default-transparent',
	prerender: true
});

interface RouteParams {
	slug: string;
}

const { trackPageView, trackBreadcrumbClicked } = useEvents();

async function getRegion(): Promise<Region | null> {
	try {
		const slug = (route.params as RouteParams).slug;

		if (!slug) return null;

		const [region] = await regionService.getOne({
			slug
		});

		return region || null;
	} catch {
		return null;
	}
}

const { user } = useUserSession();
async function getProperties(
	regionName: string,
	limit: number = 5
): Promise<Property[]> {
	try {
		const [result, error] = await nuxtPropertiesRepository.list({
			page: 1,
			perPage: limit,
			regions: [regionName],
			markUserWishlistsId: user.value?.id
		});

		if (error) {
			console.error(error);
			return [];
		}

		return result.properties.splice(0, limit);
	} catch {
		return [];
	}
}

interface RegionPageData {
	region: Region | null;
	properties: Property[];
	canonicalRedirectPath: string;
}

function getCanonicalRedirectPath(region: Region): string {
	return region.path && route.path !== region.path ? region.path : '';
}

function getDefaultRegionPageData(): RegionPageData {
	return {
		region: null,
		properties: [],
		canonicalRedirectPath: ''
	};
}

const { data: pageData, status: pageDataStatus } =
	await useAsyncData<RegionPageData>(
		`region:${(route.params as RouteParams).slug}`,
		async () => {
			const region = await getRegion();

			if (!region) {
				const event = useRequestEvent();
				if (event) {
					setResponseStatus(event, 404, 'Region not found');
				}

				throw createError({
					statusCode: 404,
					statusMessage: 'Region not found',
					fatal: true
				});
			}

			const canonicalRedirectPath = getCanonicalRedirectPath(region);

			if (canonicalRedirectPath) {
				return {
					region: null,
					properties: [],
					canonicalRedirectPath
				};
			}

			const properties = await getProperties(
				region.name,
				region.highlightedPropertiesCarousel?.propertiesLimit
			);

			return { region, properties, canonicalRedirectPath: '' };
		},
		{
			default: getDefaultRegionPageData,
			...getCacheAsyncDataConfig<RegionPageData>(30 * 60 * 1000),
			lazy: false,
			server: true,
			immediate: true
		}
	);

const isFetchingPageData = computed<boolean>(
	() => pageDataStatus.value === 'pending'
);

const region = computed<Region | null>(() => pageData.value?.region || null);
const properties = computed<Property[]>(() => pageData.value?.properties || []);

const canonicalRedirectPath = computed<string>(
	() => pageData.value?.canonicalRedirectPath || ''
);
const isRedirectingToCanonicalPath = computed<boolean>(() =>
	Boolean(canonicalRedirectPath.value)
);
const canApplyRegionSeoMeta = computed<boolean>(
	() =>
		pageDataStatus.value === 'success' &&
		!isRedirectingToCanonicalPath.value &&
		Boolean(region.value)
);

if (import.meta.server && canonicalRedirectPath.value) {
	await navigateTo(canonicalRedirectPath.value, {
		redirectCode: 301,
		replace: true
	});
}

if (import.meta.client) {
	watch(
		canonicalRedirectPath,
		path => {
			if (!path || route.path === path) return;

			void navigateTo(path, {
				redirectCode: 301,
				replace: true
			});
		},
		{ immediate: true }
	);
}

const metaTags = computed(() => region.value?.metaTags);

useSeoMeta({
	title: () =>
		canApplyRegionSeoMeta.value ? metaTags.value?.title : undefined,
	description: () =>
		canApplyRegionSeoMeta.value ? metaTags.value?.description : undefined,
	keywords: () =>
		canApplyRegionSeoMeta.value ? metaTags.value?.keywords : undefined,
	ogTitle: () =>
		canApplyRegionSeoMeta.value ? metaTags.value?.ogTitle : undefined,
	ogDescription: () =>
		canApplyRegionSeoMeta.value ? metaTags.value?.ogDescription : undefined,
	ogImage: () =>
		canApplyRegionSeoMeta.value ? metaTags.value?.ogImage : undefined,
	twitterTitle: () =>
		canApplyRegionSeoMeta.value ? metaTags.value?.twitterTitle : undefined,
	twitterDescription: () =>
		canApplyRegionSeoMeta.value
			? metaTags.value?.twitterDescription
			: undefined,
	twitterImage: () =>
		canApplyRegionSeoMeta.value ? metaTags.value?.twitterImage : undefined
});

const regionName = computed<string>(() => region.value?.name || '');
const city = computed<string>(() => region.value?.city || '');
const state = computed<string>(() => region.value?.state || '');

const heroSection = computed<Exclude<Region['hero'], undefined>>(() =>
	region.value?.hero
		? {
				...region.value.hero,
				isLoading: isFetchingPageData.value
			}
		: {
				title: '',
				description: '',
				backgroundImage: '',
				isLoading: isFetchingPageData.value
			}
);

const mapSection = computed<Region['map'] & { name: Region['name'] }>(() =>
	region.value?.map
		? { ...region.value.map, name: regionName.value }
		: {
				name: '',
				title: '',
				content: '',
				image: '',
				links: []
			}
);

const contentSection = computed<RegionContent[]>(
	() => region.value?.content || []
);

const tileSection = computed<(NearRegion & { regionName: Region['name'] })[]>(
	() =>
		region.value?.nearRegions
			? region.value?.nearRegions.map(nearRegion => ({
					...nearRegion,
					regionName: nearRegion.name
				}))
			: []
);

const questionsSection = computed<Region['questions']>(
	() => region.value?.questions || []
);

const seoSection = computed<Region['seo']>(() => region.value?.seo);

const breadcrumbItems = computed<BreadcrumbItem[]>(() => {
	if (!region.value) return [];

	return [
		{ label: 'Início', route: '/' },
		{ label: region.value?.name, route: region.value?.path }
	];
});

function handleBreadcrumbClicked(item: BreadcrumbItem) {
	trackBreadcrumbClicked({
		[MixpanelProps.CURRENT_PAGE]: route.fullPath,
		[MixpanelProps.CTA_TEXT]: item.label,
		[MixpanelProps.DESTINATION_URL]: String(item.route),
		[MixpanelProps.CLICK_HOME]: item.route === '/'
	});
}

if (import.meta.client) {
	let stopTrackPageViewWatch: (() => void) | undefined;

	onMounted(() => {
		let hasTrackedPageView = false;

		stopTrackPageViewWatch = watch(
			canApplyRegionSeoMeta,
			shouldTrackPageView => {
				if (!shouldTrackPageView || hasTrackedPageView) return;

				hasTrackedPageView = true;
				trackPageView({
					current_page: 'Detalhe do Bairro',
					previous_page_url: document.referrer
				});
			},
			{ immediate: true }
		);
	});

	onUnmounted(() => {
		stopTrackPageViewWatch?.();
	});
}
</script>
<template>
	<div v-if="!isRedirectingToCanonicalPath" class="flex flex-col">
		<RegionHero v-bind="heroSection" />

		<Container
			v-show="breadcrumbItems"
			class="bg-white py-4 md:py-6"
			:main-class="{
				'w-[225px] !mx-0': isFetchingPageData
			}"
		>
			<Breadcrumb
				:items="breadcrumbItems"
				:is-loading="isFetchingPageData"
				@breadcrumb-clicked="handleBreadcrumbClicked"
			/>
		</Container>

		<RegionMap :is-loading="isFetchingPageData" v-bind="mapSection" />

		<RegionContentSkeleton v-if="isFetchingPageData" is-loading />

		<RegionContent
			v-for="(content, index) in contentSection"
			v-else
			:key="`${content.info.title}-content`"
			v-bind="content"
			:content-index="index"
		/>

		<SectionsHighlightedRegionPropertiesSection
			v-if="properties.length || isFetchingPageData"
			:properties="properties"
			:region="regionName"
			:city="city"
			:state="state"
			:is-loading="isFetchingPageData"
			section="selected properties"
		/>

		<SectionsGridTilesSection v-if="tileSection.length && !isFetchingPageData">
			<RegionTile
				v-for="(nearRegion, index) in tileSection"
				:key="`region-tile-${nearRegion.name}`"
				:current-neighborhood="nearRegion.regionName"
				:name="nearRegion.name"
				:image="nearRegion.image"
				:href="nearRegion.href"
				:position="index + 1"
			/>
		</SectionsGridTilesSection>

		<Container
			v-if="questionsSection.length && !isFetchingPageData"
			class="bg-white-dark-1"
		>
			<RegionQuestions
				:name="regionName"
				:questions="questionsSection"
				:title="`Perguntas Frequentes sobre ${regionName}`"
				class="py-0"
				content-block-variant="white-dark-1"
				accordion-item-class="bg-white"
			/>
		</Container>

		<Container
			v-if="seoSection && !isFetchingPageData"
			class="bg-white-dark-2 py-8 md:py-15"
		>
			<div class="flex flex-col gap-6">
				<span
					class="font-matter-sq text-2xl font-normal tracking-[-0.03rem] text-primary md:text-[1.75rem] md:tracking-[-0.035rem]"
				>
					{{ seoSection.title }}
				</span>

				<NavigationSectionNavigation
					class="py-0!"
					:card="null"
					:items="seoSection.links"
					color="primary"
					links-container-variant="seo"
					links-title-classes="min-h-[36px]"
				/>
			</div>
		</Container>
	</div>
</template>
